import React, { FC } from 'react';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import {
  Commentary,
  CommentaryTypeLabel,
  UserCommentary,
  CommentaryFormat,
} from '../commentary.types';
import { RenderHighlightedParts } from '../../components/highlight/RenderHighlightedParts';
import { Highlight } from '../../components/highlight/Highlight';
import { MaterialIcon } from '../../components/material-icon/MaterialIcon';
import styles from './commentary_link_content.module.scss';

type Props = {
  commentary: Commentary | UserCommentary;
  isDoctrineLink?: boolean;
  small: boolean;
};

export const CommentaryLinkContent: FC<Props> = ({
  commentary,
  isDoctrineLink = false,
  small,
}) => (
  <span className={styles.container}>
    <span className={styles.title}>
      <span className={classNames(styles.link, small && styles.titleSmall)}>
        <RenderHighlightedParts
          config={{
            preTag: 'highlight_start',
            postTag: 'highlight_stop',
          }}
          renderHighlightedParts={renderCallback}
        >
          {commentary.title}
        </RenderHighlightedParts>
      </span>
      {!isDoctrineLink && (
        <span className={styles.info}>
          {commentary.typeLabel === CommentaryTypeLabel.HYBRID && (
            <span className={styles.text}>
              <FormattedMessage id="CommentaryLinkContent/1" />
            </span>
          )}
          <MaterialIcon
            className={classNames(styles.icon, small && styles.iconSmall)}
            name={getIconClassname(commentary)}
          />
        </span>
      )}
    </span>
    <span
      className={classNames(
        styles.subtext,
        commentary.typeLabel === CommentaryTypeLabel.PAPER && styles.paper,
      )}
    >
      {commentary.author && (
        <span className={styles.item}>{commentary.author}</span>
      )}

      {commentary.sourceName && (
        <span className={styles.item}>{commentary.sourceName}</span>
      )}
      {isDoctrineLink && (
        <span className={styles.item}>
          <FormattedMessage id="CommentaryLinkContent/2" />
        </span>
      )}
      {commentary.dateHtml && (
        <span
          className={classNames(styles.date, {
            [styles.datePaper]:
              commentary.typeLabel === CommentaryTypeLabel.PAPER,
            [styles.paper]:
              commentary.author || commentary.sourceName || isDoctrineLink,
          })}
          dangerouslySetInnerHTML={{
            __html: commentary.dateHtml,
          }}
        />
      )}
    </span>
  </span>
);

function getIconClassname(commentary: Commentary | UserCommentary) {
  if (commentary.typeLabel === CommentaryTypeLabel.PAPER) {
    return 'lock';
  }
  if (commentary.typeLabel === CommentaryTypeLabel.HYBRID) {
    return 'lock_open';
  }
  if (
    commentary.format === CommentaryFormat.HTML ||
    commentary.typeLabel === CommentaryTypeLabel.USER_GENERATED
  ) {
    return 'launch';
  }
  return 'picture_as_pdf';
}

function renderCallback(
  {
    value,
    highlighted,
  }: {
    value: string;
    highlighted: boolean;
  },
  index: number,
) {
  if (highlighted) return <Highlight key={index}>{value}</Highlight>;
  return value;
}
